import {
  APP_CHUNK_PATH,
  OWN_PARSE,
  CHUNK_FAILURE_KEY,
  CHUNK_FAILURE_MESSAGES,
  HYDRATION_RETRY_KEY,
} from "./chunkFailure";

/**
 * The watchdog that runs before the application does.
 *
 * The page is served rendered: every control is on screen before the bundle
 * attaches to it, so a bundle that never arrives leaves a page that looks
 * finished and answers nothing — no way out, no settings, no room code. This
 * script imports nothing, so it cannot fail the way the bundle did.
 *
 * **The retry policy, in full.** One reload per browsing session, and no more.
 * The reload is what recovers the common trigger: a publish replaces the hashed
 * chunk the held document names, and only a fresh document names the new one —
 * re-fetching the same URL would 404 again for as long as the visitor is on
 * that document. The budget is one because a reload that did not help would
 * otherwise loop, and it is kept in `sessionStorage` so it survives the reload
 * it is counting. Spent, the visitor gets the banner instead, whose Reload
 * button returns the budget: that press is a person deciding to try again, not
 * the page deciding for them. A browser that refuses session storage gets no
 * automatic reload at all — a budget that cannot be recorded is a loop.
 *
 * Nothing here can report the failure — the reporter is in the bundle that did
 * not load — so the script leaves what it knows under `CHUNK_FAILURE_KEY` and
 * the app sends it after the reload that recovered.
 *
 * It is generated rather than written inline so that the keys and the message
 * list have one home and the behaviour has a test.
 */
export function hydrationWatchdogScript(): string {
  const retryKey = JSON.stringify(HYDRATION_RETRY_KEY);
  const reportKey = JSON.stringify(CHUNK_FAILURE_KEY);
  const phrases = JSON.stringify([...CHUNK_FAILURE_MESSAGES]);
  const chunkPath = JSON.stringify(APP_CHUNK_PATH);
  const ownParse = OWN_PARSE.toString();

  return `(function(){try{
var RETRY_KEY=${retryKey};
var REPORT_KEY=${reportKey};
var PHRASES=${phrases};
var CHUNK_PATH=${chunkPath};
var OWN_PARSE=${ownParse};
function alive(){return document.documentElement.getAttribute("data-hydrated")==="true";}
function remember(chunk,attempts){
  try{sessionStorage.setItem(REPORT_KEY,JSON.stringify({chunk:chunk||"unknown",path:location.pathname,phase:"boot",attempts:attempts}));}catch(e){}
}
function banner(){
  if(alive()||document.getElementById("hydration-fallback"))return;
  var box=document.createElement("div");
  box.id="hydration-fallback";
  box.setAttribute("role","alert");
  box.style.cssText="position:fixed;inset:0;z-index:2147483647;display:flex;align-items:center;justify-content:center;padding:24px;background:rgba(10,10,10,.92);color:#fff;font:16px/1.5 system-ui,-apple-system,Segoe UI,sans-serif;text-align:center";
  var inner=document.createElement("div");
  inner.style.cssText="max-width:24rem";
  var h=document.createElement("p");
  h.textContent="This page did not finish loading.";
  h.style.cssText="margin:0 0 8px;font-weight:600";
  var p=document.createElement("p");
  p.textContent="Nothing on it will respond until it does. Reload, or go back to the start.";
  p.style.cssText="margin:0 0 20px;opacity:.75;font-size:14px";
  var row=document.createElement("div");
  row.style.cssText="display:flex;gap:8px;justify-content:center;flex-wrap:wrap";
  var reload=document.createElement("button");
  reload.type="button";reload.textContent="Reload";
  reload.style.cssText="padding:10px 18px;border-radius:9999px;border:0;background:#f97316;color:#fff;font:inherit;font-size:14px;cursor:pointer";
  reload.onclick=function(){try{sessionStorage.removeItem(RETRY_KEY);}catch(e){}location.reload();};
  var home=document.createElement("a");
  home.href="/";home.textContent="Go home";
  home.style.cssText="padding:10px 18px;border-radius:9999px;border:1px solid rgba(255,255,255,.3);color:#fff;text-decoration:none;font-size:14px";
  row.appendChild(reload);row.appendChild(home);
  inner.appendChild(h);inner.appendChild(p);inner.appendChild(row);
  box.appendChild(inner);
  (document.body||document.documentElement).appendChild(box);
}
function recover(chunk){
  if(alive())return;
  // The budget is the reload's only stop, so a storage that cannot hold it is
  // a reload that would repeat forever. Then the banner is the whole recovery.
  var budget=false;
  try{if(sessionStorage.getItem(RETRY_KEY)===null){sessionStorage.setItem(RETRY_KEY,"1");budget=true;}}catch(e){}
  remember(chunk,budget?1:2);
  if(budget){location.reload();return;}
  banner();
}
function isChunkFailure(m){
  for(var i=0;i<PHRASES.length;i++){if(m.indexOf(PHRASES[i])>-1)return true;}
  return false;
}
function urlIn(m){var found=/https?:[^ '")<>]+/.exec(m);return found?found[0]:"";}
window.addEventListener("error",function(ev){
  var target=ev&&ev.target;
  // A module script that 404s reports here with no message at all; the element
  // is the only thing that names the file. Only this application's own chunks
  // count, and those are the hashed files under CHUNK_PATH: a same-origin script
  // elsewhere on the origin is one an extension or an in-app browser injected,
  // and — like an image that would not load or a blocked advertising tag — is no
  // reason to reload the page.
  var src=(target&&target.tagName==="SCRIPT"&&target.src)||"";
  var asset=src.indexOf(location.origin+CHUNK_PATH)===0?src:"";
  if(asset){setTimeout(function(){recover(asset);},100);return;}
  // A chunk that fetched but is not valid JavaScript throws here as a plain
  // SyntaxError, not a script-load error — the element never named it, so the
  // file the error came from is the only same-origin signal left. A syntax
  // error anywhere else on the page is not this application's build, and one
  // its own code raised parsing data (OWN_PARSE) is not a chunk: neither is a
  // reason to reload.
  var filename=(ev&&ev.filename)||"";
  var parseFailure=!!(ev&&ev.error&&ev.error.name==="SyntaxError"&&!OWN_PARSE.test(ev.error.message||"")&&filename.indexOf(location.origin+CHUNK_PATH)===0);
  if(parseFailure){setTimeout(function(){recover(filename);},100);return;}
  var m=(ev&&(ev.message||(ev.error&&ev.error.message)))||"";
  if(isChunkFailure(m))setTimeout(function(){recover(urlIn(m));},100);
},true);
window.addEventListener("unhandledrejection",function(ev){
  var r=ev&&ev.reason;var m=(r&&(r.message||String(r)))||"";
  if(isChunkFailure(m))setTimeout(function(){recover(urlIn(m));},100);
});
// Vite's own preload helper reports a failed chunk here before anything else
// sees it — the earliest, cheapest place to recover from a dropped asset.
window.addEventListener("vite:preloadError",function(ev){
  var payload=ev&&ev.payload;
  setTimeout(function(){recover((payload&&payload.message&&urlIn(payload.message))||"");},100);
});
window.addEventListener("load",function(){
  setTimeout(function(){
    if(alive()){try{sessionStorage.removeItem(RETRY_KEY);}catch(e){}return;}
    recover("");
  },4000);
});
}catch(e){}})();`;
}
